import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import Breadcrumb, { BreadcrumbListSchema } from '@/components/base/Breadcrumb';
import { WebPageSchema } from '@/components/base/SEOSchemas';
import { comics, comicCategories } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeTab, setActiveTab] = useState(slug || 'all');

  useEffect(() => {
    if (slug) setActiveTab(slug);
  }, [slug]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeTab]);

  const filteredComics = useMemo(() => {
    if (activeTab === 'all') return comics;
    return comics.filter((c) => c.category === activeTab);
  }, [activeTab]);

  const currentCategory = comicCategories.find((c) => c.slug === activeTab);
  const categoryName = currentCategory?.name || '全部';

  const breadcrumbItems = [
    { name: '首页', href: '/' },
    { name: '分类浏览', href: '/category/all' },
    { name: categoryName, href: `/category/${activeTab}` },
  ];

  return (
    <>
      <WebPageSchema
        name={`${categoryName}漫画 - 飞飞漫画网`}
        description={`飞飞漫画网${categoryName}漫画分类，为您精选最优质的${categoryName}类韩国漫画作品，海量资源，畅快阅读。`}
        url={`/category/${activeTab}`}
      />
      <BreadcrumbListSchema items={breadcrumbItems} />

      <Navbar />

      <main className="pt-20">
        {/* Header */}
        <section className="py-10 px-4 md:px-6 bg-background-50">
          <div className="w-full max-w-7xl mx-auto">
            <Breadcrumb items={breadcrumbItems} />
            <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mt-2">
              {categoryName}漫画
            </h1>
            <p className="text-sm text-foreground-500 mt-2">
              共找到 <strong className="text-primary-500">{filteredComics.length}</strong> 部作品
            </p>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="sticky top-16 z-30 bg-background-50/95 backdrop-blur-md border-b border-background-200/60 px-4 md:px-6 py-3">
          <div className="w-full max-w-7xl mx-auto">
            <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide flex-wrap">
              {comicCategories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  onClick={() => setActiveTab(cat.slug)}
                  className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 cursor-pointer ${
                    activeTab === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Comic Grid */}
        <section className="py-10 px-4 md:px-6 bg-background-50">
          <div className="w-full max-w-7xl mx-auto">
            {filteredComics.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <i className="ri-emotion-sad-line text-4xl text-foreground-300"></i>
                </div>
                <p className="text-foreground-500 text-sm">该分类暂无漫画，请浏览其他分类</p>
                <Link
                  to="/category/all"
                  className="whitespace-nowrap inline-flex items-center gap-2 mt-4 text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-left-line"></i> 返回全部漫画
                </Link>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-5">
                {filteredComics.map((comic) => (
                  <ComicCard key={`cat-${comic.id}`} comic={comic} variant="compact" />
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="py-12 px-4 md:px-6 bg-background-100">
          <div className="w-full max-w-7xl mx-auto text-center">
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-3">
              没找到想看的？
            </h2>
            <p className="text-sm text-foreground-500 mb-6">
              我们每天都在更新新的漫画资源，敬请期待更多精彩内容！
            </p>
            <Link
              to="/"
              className="whitespace-nowrap inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white px-6 py-2.5 rounded-full text-sm font-medium transition-colors cursor-pointer"
            >
              返回首页探索更多
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}